/**
 * MentionAutocomplete — WhatsApp-style participant mention dropdown.
 *
 * Shown when the user types `@` in the ChatComposer textarea. Displays a
 * filtered list of participants; selecting one inserts `@FirstName ` into the
 * textarea. Keyboard navigation: ↑/↓ to move, Enter/Tab to select, Escape to
 * dismiss.
 */

import { useEffect, useRef, useState } from 'react'
import { PersonaThumbImg } from '@/components/shared/PersonaThumbImg'
import { cn } from '@/lib/utils'

export interface MentionParticipant {
  id: string
  displayName: string
  /** Inserted token (without `@`) when it must not follow the shown name, e.g. `all` under any UI language. */
  mentionToken?: string
  imageUrl: string | null
}

interface MentionAutocompleteProps {
  participants: Array<MentionParticipant>
  query: string
  onSelect: (displayName: string) => void
  onDismiss: () => void
}

function getFirstName(displayName: string): string {
  return displayName.split(/\s+/)[0]
}

function getInitials(name: string): string {
  const parts = name.split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase()
  return name.slice(0, 2).toUpperCase()
}

// One row's avatar, pulled into its own component so it can hold the
// thumbnail/original fallback state per-participant (rules of hooks forbid
// calling useState inside the list's `.map`).
function MentionAvatar(props: { participant: MentionParticipant }) {
  const { participant } = props
  const [imageFailed, setImageFailed] = useState(false)

  if (participant.imageUrl && !imageFailed) {
    return (
      <PersonaThumbImg
        src={participant.imageUrl}
        size={64}
        alt=""
        className="h-7 w-7 rounded-full object-cover shrink-0"
        onFinalError={() => setImageFailed(true)}
      />
    )
  }

  return (
    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-twin-chat-fg/15 text-xs font-medium text-twin-chat-fg shrink-0">
      {getInitials(participant.displayName)}
    </div>
  )
}

export function MentionAutocomplete(props: MentionAutocompleteProps) {
  const { participants, query, onSelect, onDismiss } = props
  const [activeIndex, setActiveIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const filtered = participants.filter((p) => {
    if (!query) return true
    const q = query.toLowerCase()
    const parts = p.displayName.toLowerCase().split(/\s+/)
    const full = p.displayName.toLowerCase()
    // Match on any name part (first, last, etc.) or the full name
    return parts.some((part) => part.startsWith(q)) || full.startsWith(q)
  })

  // Reset active index whenever the SET of matches changes — not just its
  // length. Two different queries can both match, say, 3 participants; keying
  // off `filtered.length` alone left the highlight sitting on whatever index
  // it was already at, which could now point at a different person than the
  // one the query narrowed down to.
  const filteredKey = filtered.map((p) => p.id).join(',')
  useEffect(() => {
    setActiveIndex(0)
  }, [filteredKey])

  // Scroll active item into view
  useEffect(() => {
    const el = listRef.current?.children[activeIndex] as HTMLElement | undefined
    el?.scrollIntoView({ block: 'nearest' })
  }, [activeIndex])

  if (filtered.length === 0) return null

  return (
    <div
      ref={listRef}
      className="floating-surface absolute bottom-full left-0 right-0 z-50 mb-1 max-h-48 overflow-y-auto rounded-xl border border-white/10 bg-twin-chat-surface shadow-lg"
      data-testid="mention-autocomplete"
    >
      {filtered.map((p, i) => (
        <button
          key={p.id}
          type="button"
          className={cn(
            'flex w-full items-center gap-3 px-4 py-2.5 text-left text-sm transition-colors',
            i === activeIndex
              ? 'bg-twin-chat-fg/10 text-twin-chat-fg'
              : 'text-twin-chat-fg-muted hover:bg-twin-chat-fg/5',
          )}
          onMouseDown={(e) => {
            // Prevent textarea blur
            e.preventDefault()
            onSelect(p.displayName)
          }}
          onMouseEnter={() => setActiveIndex(i)}
        >
          <MentionAvatar participant={p} />
          <span className="truncate font-medium">{p.displayName}</span>
        </button>
      ))}
    </div>
  )
}

/**
 * Hook to manage mention state inside a textarea.
 *
 * Returns mention state + handlers to wire into the textarea's onChange and
 * onKeyDown events.
 */
/**
 * Returns the shortest unique mention token for a participant.
 * Uses first name unless another participant shares the same first name,
 * in which case it returns "FirstLast" (no space, so it's a single @token).
 */
export function getMentionToken(participant: MentionParticipant, allParticipants: Array<MentionParticipant>): string {
  if (participant.mentionToken) {
    return participant.mentionToken
  }
  const firstName = getFirstName(participant.displayName)
  const hasDuplicate = allParticipants.some(
    (p) => p.id !== participant.id && getFirstName(p.displayName).toLowerCase() === firstName.toLowerCase(),
  )
  if (hasDuplicate) {
    // Use FirstLast (no space) so it stays as a single @mention token
    const parts = participant.displayName.split(/\s+/)
    return parts.length >= 2 ? parts[0] + parts[parts.length - 1] : firstName
  }
  return firstName
}

/**
 * The text a picked mention puts in the composer: `@token ` (trailing space). The one
 * insertion format, shared by the @ menu and the profile card's Ask action.
 */
export function formatMentionInsertion(token: string): string {
  return `@${token} `
}

export function useMentionAutocomplete(
  participants: Array<MentionParticipant>,
  value: string,
  setValue: (v: string) => void,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>,
  /** Called with the picked participant id and the inserted token (without `@`). */
  onInsert?: (participantId: string, token: string) => void,
) {
  const [mentionState, setMentionState] = useState<{
    active: boolean
    query: string
    startIndex: number
  } | null>(null)

  const filteredParticipants = mentionState
    ? participants.filter((p) => {
        if (!mentionState.query) return true
        const q = mentionState.query.toLowerCase()
        const parts = p.displayName.toLowerCase().split(/\s+/)
        const full = p.displayName.toLowerCase()
        return parts.some((part) => part.startsWith(q)) || full.startsWith(q)
      })
    : []

  const activeIndexRef = useRef(0)
  const [, forceRender] = useState(0)

  function handleChange(newValue: string, cursorPos: number) {
    // Look backwards from cursor for an @ trigger
    const textBeforeCursor = newValue.slice(0, cursorPos)
    const atMatch = textBeforeCursor.match(/@(\w*)$/)

    if (atMatch) {
      const startIndex = textBeforeCursor.length - atMatch[0].length
      const query = atMatch[1]
      setMentionState({ active: true, query, startIndex })
      activeIndexRef.current = 0
    } else {
      setMentionState(null)
    }
  }

  function handleSelect(displayName: string) {
    if (!mentionState || !textareaRef.current) return

    // Resolve the participant to get the right mention token
    const participant = participants.find((p) => p.displayName === displayName)
    const token = participant ? getMentionToken(participant, participants) : getFirstName(displayName)

    const before = value.slice(0, mentionState.startIndex)
    const after = value.slice(
      mentionState.startIndex + 1 + mentionState.query.length,
    )
    const newValue = `${before}${formatMentionInsertion(token)}${after}`
    setValue(newValue)
    setMentionState(null)
    if (participant) {
      onInsert?.(participant.id, token)
    }

    // Move cursor after the inserted mention
    const cursorPos = before.length + formatMentionInsertion(token).length
    requestAnimationFrame(() => {
      textareaRef.current?.setSelectionRange(cursorPos, cursorPos)
      textareaRef.current?.focus()
    })
  }

  function handleKeyDown(e: React.KeyboardEvent): boolean {
    if (!mentionState?.active || filteredParticipants.length === 0) return false

    if (e.key === 'ArrowDown') {
      e.preventDefault()
      activeIndexRef.current = Math.min(
        activeIndexRef.current + 1,
        filteredParticipants.length - 1,
      )
      forceRender((n) => n + 1)
      return true
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      activeIndexRef.current = Math.max(activeIndexRef.current - 1, 0)
      forceRender((n) => n + 1)
      return true
    }
    if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      const selected = filteredParticipants[activeIndexRef.current]
      if (selected) handleSelect(selected.displayName)
      return true
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setMentionState(null)
      return true
    }
    return false
  }

  function dismiss() {
    setMentionState(null)
  }

  return {
    mentionState,
    filteredParticipants,
    activeIndex: activeIndexRef.current,
    handleChange,
    handleSelect,
    handleKeyDown,
    dismiss,
  }
}
